import { useEffect, useMemo, useState } from 'react'
import { FlaskConical, AlertTriangle, Loader2, ArrowDownLeft, ArrowUpRight } from 'lucide-react'
import { TM_SCENARIOS, CONFIG_CHECKLISTS, DEFAULT_TM_RULE_CONFIG } from '../data'
import { runTmSimulation } from '../utils'
import { ConfiguringChecklist } from './ConfiguringChecklist'
import { InteractionDiagram } from './InteractionDiagram'
import { TmConfigPanel } from './TmConfigPanel'
import { TmGuidedView } from './TmGuidedView'
import { ExperienceModeSwitcher } from './ExperienceModeSwitcher'
import type { ExperienceMode } from './ExperienceModeSwitcher'
import { ExistingCustomerPanel } from './ExistingCustomerPanel'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import type { CustomerMode } from './NewExistingToggle'
import { StageSaveBar } from './StageSaveBar'
import { buildTmDiagramDsl } from '../dynamicInteractionDiagrams'
import type { PlatformJourneyRecord } from '@api/client'
import type { SimAlert, TmRuleConfig } from '../types'
import { useAiSeed } from '../aiConfigure'
import type { AiConfigureSuggestions, AiStageSuggestionState } from '../aiConfigure'
import { AiSuggestionBanner } from './AiSuggestionBanner'

const SEVERITY_STYLE: Record<SimAlert['severity'], { bg: string; border: string; color: string }> = {
  LOW: { bg: 'rgba(52,211,153,.08)', border: 'rgba(52,211,153,.3)', color: '#15803d' },
  MEDIUM: { bg: 'rgba(251,146,60,.08)', border: 'rgba(251,146,60,.3)', color: '#c2410c' },
  HIGH: { bg: 'rgba(248,113,113,.08)', border: 'rgba(248,113,113,.35)', color: '#b91c1c' },
}

interface TransactionMonitoringStageProps {
  onRunningChange: (running: boolean) => void
  onComplete: (alerts: SimAlert[]) => void
  customerMode: CustomerMode
  existingCustomer: ExistingCustomerSelection | null
  customerName: string | null
  experienceMode: ExperienceMode
  onExperienceModeChange: (mode: ExperienceMode) => void
  journeyId: number | null
  journeyRecord: PlatformJourneyRecord | null
  onJourneySaved: () => void
  aiSuggestions?: AiConfigureSuggestions | null
  /** Lifted to the tool shell so Account Setup's checklist and this stage's
   * own banner share one apply/dismiss state instead of drifting apart. */
  tmAi: AiStageSuggestionState<TmRuleConfig>
}

export function TransactionMonitoringStage({
  onRunningChange, onComplete, customerMode, existingCustomer, customerName,
  experienceMode, onExperienceModeChange, journeyId, journeyRecord, onJourneySaved,
  aiSuggestions, tmAi,
}: TransactionMonitoringStageProps) {
  const [scenarioId, setScenarioId] = useState(TM_SCENARIOS[0].id)
  const [ruleConfig, setRuleConfig] = useState<TmRuleConfig>(DEFAULT_TM_RULE_CONFIG)
  useAiSeed(tmAi.seed, setRuleConfig)
  const [checklistEnabled, setChecklistEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONFIG_CHECKLISTS.tm.map((i) => [i.id, true]))
  )
  function toggleChecklistItem(id: string) {
    setChecklistEnabled((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  useEffect(() => {
    if (!journeyRecord) return
    if (journeyRecord.tmConfig) {
      const cfg = journeyRecord.tmConfig as { scenarioId?: string; ruleConfig?: TmRuleConfig }
      if (cfg.scenarioId) setScenarioId(cfg.scenarioId)
      if (cfg.ruleConfig) setRuleConfig(cfg.ruleConfig)
    }
    if (journeyRecord.tmChecklist) setChecklistEnabled(journeyRecord.tmChecklist as Record<string, boolean>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyRecord?.id])
  const [running, setRunning] = useState(false)
  const [revealedCount, setRevealedCount] = useState(0)
  const [alerts, setAlerts] = useState<SimAlert[] | null>(null)

  const scenario = TM_SCENARIOS.find(s => s.id === scenarioId) ?? TM_SCENARIOS[0]
  const diagramDsl = useMemo(() => buildTmDiagramDsl(ruleConfig), [ruleConfig])

  function selectScenario(id: string) {
    setScenarioId(id)
    setAlerts(null)
    setRevealedCount(0)
  }

  async function run() {
    setRunning(true)
    onRunningChange(true)
    setAlerts(null)
    setRevealedCount(0)
    for (let i = 1; i <= scenario.transactions.length; i++) {
      await new Promise(r => setTimeout(r, 450))
      setRevealedCount(i)
    }
    await new Promise(r => setTimeout(r, 300))
    const result = runTmSimulation(scenario.transactions, ruleConfig)
    setAlerts(result)
    setRunning(false)
    onRunningChange(false)
    onComplete(result)
  }

  const tmAiBanner = tmAi.visible && aiSuggestions?.tm && (
    <AiSuggestionBanner
      rationale={aiSuggestions.tm.rationale}
      confidence={aiSuggestions.tm.confidence}
      applied={tmAi.applied}
      onApply={tmAi.apply}
      onDismiss={tmAi.dismiss}
    />
  )

  const technicalDetailsNode = (
    <>
      {tmAiBanner}
      <TmConfigPanel config={ruleConfig} onChange={setRuleConfig} />
      <InteractionDiagram stageId="tm" dsl={diagramDsl} />
      {revealedCount > 0 && (
        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
          <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Transaction Feed</p>
          </div>
          <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
            {scenario.transactions.slice(0, revealedCount).map(tx => {
              const flagged = alerts?.some(a => a.transactionId === tx.id)
              return (
                <div key={tx.id} className="flex items-center gap-3 px-4 py-2.5">
                  {tx.direction === 'OUT' ? <ArrowUpRight size={13} className="text-amber-700 shrink-0" /> : <ArrowDownLeft size={13} className="text-emerald-700 shrink-0" />}
                  <span className="text-[11px] text-[#475569] flex-1 truncate">{tx.counterparty}</span>
                  <span className="text-[10px] font-mono text-[#64748b]">{tx.channel}</span>
                  <span className="text-[11px] font-mono font-semibold text-[#1e293b] w-24 text-right">{tx.currency} {tx.amount.toLocaleString()}</span>
                  {flagged && <AlertTriangle size={13} className="text-red-700 shrink-0" />}
                </div>
              )
            })}
          </div>
        </div>
      )}
      {alerts !== null && alerts.length > 0 && (
        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
          <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">{alerts.length} rule{alerts.length !== 1 ? 's' : ''} triggered</p>
          </div>
          <div className="p-3 space-y-2">
            {alerts.map(a => {
              const style = SEVERITY_STYLE[a.severity]
              return (
                <div key={a.id} className="flex items-start gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: style.bg, border: `1px solid ${style.border}` }}>
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" style={{ color: style.color }} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold" style={{ color: style.color }}>{a.rule}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${style.color}20`, color: style.color }}>{a.severity}</span>
                    </div>
                    <p className="text-[11px] text-[#475569] mt-0.5">{a.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )

  return (
    <div className="space-y-3">
      {customerMode === 'existing' ? (
        <div className="space-y-3">
          <ExistingCustomerPanel
            productKey="tm"
            sections={['Rules']}
            title="Transaction Monitoring — Existing Customer"
            description="Shows this customer's Rules & Fraud settings as scoped in DealHub, including whether transaction & activity monitoring is in scope. Reads DealHub's own presales record, not FrankieOne's live platform."
            showCascadeSummary={false}
            sharedSelection={existingCustomer}
            searchable={false}
          />
          {tmAiBanner}
          <TmConfigPanel config={ruleConfig} onChange={setRuleConfig} />
        </div>
      ) : (
      <>
      <ExperienceModeSwitcher mode={experienceMode} onChange={onExperienceModeChange} />
      <StageSaveBar
        journeyId={journeyId}
        buildFields={() => ({ tmConfig: { scenarioId, ruleConfig }, tmChecklist: checklistEnabled })}
        onSaved={onJourneySaved}
      />

      {experienceMode === 'guided' && (
        <TmGuidedView
          scenarioId={scenarioId}
          onSelectScenario={selectScenario}
          onRun={() => void run()}
          running={running}
          alerts={alerts}
          customerName={customerName}
          checklistItems={CONFIG_CHECKLISTS.tm}
          checklistEnabled={checklistEnabled}
          onToggleChecklistItem={toggleChecklistItem}
          technicalDetails={technicalDetailsNode}
        />
      )}

      {experienceMode === 'classic' && (
      <>
      <ConfiguringChecklist productLabel="Transaction Monitoring" items={CONFIG_CHECKLISTS.tm} enabled={checklistEnabled} onToggle={toggleChecklistItem} customerName={customerName} />
      {tmAiBanner}
      <TmConfigPanel config={ruleConfig} onChange={setRuleConfig} />
      <InteractionDiagram stageId="tm" dsl={diagramDsl} />

      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(320px, 380px) 1fr' }}>
        <div className="rounded-[14px] overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
          <div className="px-4 py-3" style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
            <div className="flex items-center gap-2">
              <p className="text-[12px] font-semibold text-[#1e293b]">Transaction Monitoring</p>
              <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: 'rgba(167,139,250,.12)', color: '#7c3aed' }}>
                <FlaskConical size={8} /> Simulated
              </span>
            </div>
            <p className="text-[10px] text-[#64748b] mt-1 leading-snug">
              FrankieOne doesn&apos;t have a live Transaction Monitoring endpoint wired into this demo yet — this stage evaluates a small illustrative ruleset against sample transactions client-side, so it&apos;s honest about not being a live API call.
            </p>
          </div>
          <div className="p-4 space-y-3">
            <div className="space-y-1.5">
              {TM_SCENARIOS.map(s => (
                <button
                  key={s.id}
                  onClick={() => { setScenarioId(s.id); setAlerts(null); setRevealedCount(0) }}
                  className="w-full text-left px-3 py-2 rounded-[9px] transition-colors"
                  style={scenarioId === s.id
                    ? { background: 'rgba(99,102,241,.14)', border: '1px solid rgba(99,102,241,.4)' }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb' }
                  }
                >
                  <p className="text-[11px] font-semibold text-[#1e293b]">{s.label}</p>
                  <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{s.description}</p>
                </button>
              ))}
            </div>

            <button
              onClick={() => void run()}
              disabled={running}
              className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
            >
              {running
                ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Streaming transactions…</span>
                : 'Run Simulated Transactions'}
            </button>
          </div>
        </div>

        <div className="space-y-3 min-w-0">
          <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
            <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Transaction Feed</p>
            </div>
            {revealedCount === 0 ? (
              <p className="px-4 py-8 text-[11px] text-[#475569] italic text-center">Run the simulation to stream transactions in</p>
            ) : (
              <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
                {scenario.transactions.slice(0, revealedCount).map(tx => {
                  const flagged = alerts?.some(a => a.transactionId === tx.id)
                  return (
                    <div key={tx.id} className="flex items-center gap-3 px-4 py-2.5" style={{ animation: 'fadeUp .25s ease' }}>
                      {tx.direction === 'OUT' ? <ArrowUpRight size={13} className="text-amber-700 shrink-0" /> : <ArrowDownLeft size={13} className="text-emerald-700 shrink-0" />}
                      <span className="text-[11px] text-[#475569] flex-1 truncate">{tx.counterparty}</span>
                      <span className="text-[10px] font-mono text-[#64748b]">{tx.channel}</span>
                      <span className="text-[11px] font-mono font-semibold text-[#1e293b] w-24 text-right">{tx.currency} {tx.amount.toLocaleString()}</span>
                      {flagged && <AlertTriangle size={13} className="text-red-700 shrink-0" />}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {alerts !== null && (
            <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
              <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
                  {alerts.length === 0 ? 'No rules triggered' : `${alerts.length} rule${alerts.length !== 1 ? 's' : ''} triggered`}
                </p>
              </div>
              {alerts.length === 0 ? (
                <p className="px-4 py-3 text-[11px] text-[#475569] italic">This transaction set didn&apos;t trip any of the sample rules.</p>
              ) : (
                <div className="p-3 space-y-2">
                  {alerts.map(a => {
                    const style = SEVERITY_STYLE[a.severity]
                    return (
                      <div key={a.id} className="flex items-start gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: style.bg, border: `1px solid ${style.border}` }}>
                        <AlertTriangle size={14} className="shrink-0 mt-0.5" style={{ color: style.color }} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold" style={{ color: style.color }}>{a.rule}</span>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${style.color}20`, color: style.color }}>{a.severity}</span>
                          </div>
                          <p className="text-[11px] text-[#475569] mt-0.5">{a.description}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </>
      )}
      </>
      )}
    </div>
  )
}
